import type { LoaderFunctionArgs } from '~/types/route-args';
import type {
    GetArticleNextOpeningQuery,
    GetArticleNextOpeningQueryVariables,
} from 'generated/graphql';
import { GetArticleNextOpeningDocument } from 'generated/graphql';

export const NEXT_ARTICLE_OPENING_API_ROUTE = '/remix-api/article/next-opening';

/** What an article page opens on: its banner, and the headline over it. */
export interface NextArticleOpening {
    /** The article the opening belongs to, so a stale answer is never drawn under another article's name. */
    link: string;
    title: string;
    /** The rendition the masthead paints, so the browser has it cached once the page turns. */
    imageSrc: string;
}

/**
 * The opening an article page paints, for the foot of the page before it.
 *
 * The foot grows the next article's banner into place and then turns the
 * page, and the turn is only invisible while the banner it grew is the
 * banner the next page paints: the same rendition on the same crop, with the
 * same headline over it. Those come from the article itself rather than from
 * its listing tile, which carries a different crop of a different size.
 */
export const loader = async ({
    request,
    context,
}: LoaderFunctionArgs): Promise<NextArticleOpening | null> => {
    const link = new URL(request.url).searchParams.get('link');
    if (!link) throw new Response('Missing link', { status: 400 });

    const { data } = await context.client.query<
        GetArticleNextOpeningQuery,
        GetArticleNextOpeningQueryVariables
    >({
        query: GetArticleNextOpeningDocument,
        variables: { link },
    });

    const node = data?.Directory?.Article;
    if (node?.__typename !== 'Article') return null;

    // The masthead's own fallback: the social preview image when the banner
    // has no still, as on a video-only banner.
    const imageSrc =
        node.TopBanner?.BannerImage?.LibraryImageFile?.DetailPageBanner
            ?.Desktop ||
        node.FacebookPreviewImage ||
        '';
    if (!imageSrc) return null;

    return {
        link,
        title: node.Title,
        imageSrc,
    };
};
